/**
 * Cuerpo de una solicitud o respuesta de Bonus.
 *
 * `bonus_request` guarda los cuerpos como texto JSON. Aquí se intentan
 * interpretar para mostrarlos legibles y marcar los errores que devuelve el
 * servicio (el panel anterior dibujaba tarjetas específicas por campo; el JSON
 * formateado muestra la misma información sin duplicar lógica por tipo).
 */

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

type Parseo =
  | { tipo: 'objeto'; objeto: Record<string, unknown> }
  | { tipo: 'texto'; texto: string }
  | { tipo: 'vacio' };

function parsear(valor: string | null | undefined): Parseo {
  if (!valor || valor.trim() === '') return { tipo: 'vacio' };

  try {
    const objeto = JSON.parse(valor) as unknown;

    if (objeto && typeof objeto === 'object' && !Array.isArray(objeto)) {
      return { tipo: 'objeto', objeto: objeto as Record<string, unknown> };
    }

    return { tipo: 'texto', texto: valor };
  } catch {
    return { tipo: 'texto', texto: valor };
  }
}

/** Mensaje de error reconocible en la respuesta del servicio. */
function mensajeDeError(objeto: Record<string, unknown>): string | null {
  const codigo = objeto.statusCode ?? objeto.code;

  if (codigo && typeof objeto.message === 'string') {
    return `${String(codigo)} · ${objeto.message}`;
  }

  if (objeto.status === 'error' || objeto.status === false || objeto.success === false) {
    const mensajes = Array.isArray(objeto.messages)
      ? objeto.messages
          .map((item) => {
            if (item && typeof item === 'object') {
              const detalle = item as Record<string, unknown>;
              return [detalle.code, detalle.description].filter(Boolean).join(' · ');
            }

            return String(item);
          })
          .filter(Boolean)
          .join(' · ')
      : null;

    if (mensajes) return mensajes;
    if (typeof objeto.message === 'string') return objeto.message;

    return 'El servicio devolvió un error.';
  }

  return null;
}

interface CuerpoJsonProps {
  etiqueta: string;
  valor: string | null | undefined;
}

/** Render de un cuerpo (`Datos enviados` o `Respuesta`). */
export function CuerpoJson({ etiqueta, valor }: CuerpoJsonProps) {
  const parseo = parsear(valor);

  if (parseo.tipo === 'vacio') {
    return (
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
          {etiqueta}
        </p>
        <p className="text-sm text-ink-400">Sin datos.</p>
      </div>
    );
  }

  if (parseo.tipo === 'texto') {
    return (
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
          {etiqueta}
        </p>
        <pre className="overflow-x-auto rounded-lg bg-ink-50 p-3 text-xs leading-relaxed whitespace-pre-wrap break-words text-ink-700">
          {parseo.texto}
        </pre>
      </div>
    );
  }

  const error = mensajeDeError(parseo.objeto);
  const estado = typeof parseo.objeto.status === 'string' ? parseo.objeto.status : null;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">{etiqueta}</p>
        {estado ? (
          <Badge
            variant={estado === 'success' ? 'success' : estado === 'error' ? 'danger' : 'neutral'}
          >
            {estado}
          </Badge>
        ) : null}
      </div>

      {error ? (
        <Alert variant="error" className="mb-2">
          {error}
        </Alert>
      ) : null}

      <pre className="overflow-x-auto rounded-lg bg-ink-50 p-3 text-xs leading-relaxed whitespace-pre-wrap break-words text-ink-700">
        {JSON.stringify(parseo.objeto, null, 2)}
      </pre>
    </div>
  );
}
